<template>
  <div data-testid="fpf-table">
    <h3>{{ t('Taxas da franquia') }}</h3>
    <p class="field-hint">{{ t('O que esta franquia sobrescreve do padrão da forma de pagamento. Vale para os próximos recebimentos.') }}</p>

    <div class="fpf-franquia">
      <select-list
        elem-id="fpf_franquia"
        :label="t('Franquia')"
        v-model="franquiaId"
        :itens-select="franquiasOptions"
        data-testid="fpf-select-franquia"
      />
    </div>

    <data-table
      v-if="itens.length > 0"
      test-id-prefix="fpf"
      :config="datatableConfig"
      :data="linhas"
      @edit-click="editar">
    </data-table>
    <p v-else-if="!carregando" data-testid="fpf-vazio">{{ t('Nenhuma forma de pagamento cadastrada.') }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import SelectList from '../../inputs/SelectList.vue';
import DataTable from '../../grid/DataTable.vue';
import { eventBus } from '../../eventBus';
import { useFormasPagamento } from '../../composables/useFormasPagamento';
import { PARCELAS_MAX_PADRAO } from '../../composables/useParcelamento';
import { permissionsService } from '../../services/permissionsService';
import { diasEfetivos, parcelasEfetivas, temAplicacaoNaFranquia } from './formasPagamentoFranquia';
import { log } from '../../utils/DebugLogger';
import type { FormasPagamentoFranquia_ItemRow } from '../../contracts/FormasPagamentoFranquia_ItemRow';

const { t } = useI18n();
const { listarAplicacoes } = useFormasPagamento();

const franquiasOptions = computed(() => permissionsService.getFranquiasSelectArray());
const franquiaId = ref<string | number>(franquiasOptions.value[0]?.ID ?? '');
const itens = ref<FormasPagamentoFranquia_ItemRow[]>([]);
const carregando = ref<boolean>(false);

const rotuloParcelas = (item: FormasPagamentoFranquia_ItemRow): string => {
  const parcelas = parcelasEfetivas(item, PARCELAS_MAX_PADRAO);
  return parcelas === null ? t('Não parcela') : `${t('Até')} ${parcelas}x`;
};

const carregar = async (): Promise<void> => {
  if (!franquiaId.value) return;
  carregando.value = true;
  try {
    itens.value = await listarAplicacoes(Number(franquiaId.value));
  } catch (error) {
    log('FormasPagamentoFranquiaTable', 'carregar', 'Error', error);
    itens.value = [];
    eventBus.emit('globalMessage.error', t('Erro ao listar as formas de pagamento da franquia'));
  } finally {
    carregando.value = false;
  }
};

const linhas = computed(() => itens.value.map((item) => ({
  forma_pagamento_id: item.forma_pagamento_id,
  forma_pagamento_nome: item.forma_pagamento_nome,
  parcelas: rotuloParcelas(item),
  dias_para_receber: diasEfetivos(item),
  origem: temAplicacaoNaFranquia(item) ? t('Da franquia') : t('Padrão')
})));

const datatableConfig = computed(() => ({
  tableId: 'tabelaFormasPagamentoFranquia',
  title: t('Taxas da franquia'),
  filename: 'formas_pagamento_franquia',
  columns: [
    { data: 'forma_pagamento_id', title: t('ID'), width: 80, renderType: 'clickable_id' },
    { data: 'forma_pagamento_nome', title: t('Forma de pagamento'), width: 200 },
    { data: 'parcelas', title: t('Parcelas'), width: 120 },
    { data: 'dias_para_receber', title: t('Dias para receber'), width: 140 },
    { data: 'origem', title: t('Origem'), width: 120 }
  ],
  pageLength: 25,
  footer: false,
  rowTestId: (data: Record<string, unknown>) => `fpf-row-${data?.forma_pagamento_id}`
}));

const editar = (formaPagamentoId: number | string): void => {
  const item = itens.value.find((i) => i.forma_pagamento_id === Number(formaPagamentoId));
  if (!item) return;
  eventBus.emit('modal.formas-pagamento-franquia.open', { item, franquiaId: Number(franquiaId.value) });
};

watch(franquiaId, carregar);

onMounted(() => {
  carregar();
  eventBus.on('formasPagamentoFranquia.updated', carregar);
});
onBeforeUnmount(() => eventBus.off('formasPagamentoFranquia.updated', carregar));
</script>

<style scoped>
.fpf-franquia {
  max-width: 360px;
  margin-bottom: 16px;
}

.field-hint {
  color: var(--text-color-secondary);
}
</style>
